import { useState } from 'react';
import { clsx } from 'clsx';
import dayjs from 'dayjs';
import { Clock, User, Scissors, CreditCard, Check } from 'lucide-react';
import { CardActions } from '@/components/common/CardActions';
import { StrategyBadge } from '@/components/common/StrategyBadge';
import { WhatsAppMessagePreview } from '@/components/common/WhatsAppMessagePreview';
import { QuickTemplateSelector } from '@/components/filters/QuickTemplateSelector';
import { formatTime } from '@/utils/date';
import { formatBs, convertToBs, formatCurrency } from '@/utils/currency';
import { formatRateSource } from '@/services/rates.service';
import {
  messageForAppointment,
  getDefaultTemplateKeyForAppointment,
  APPOINTMENT_TEMPLATE_OPTIONS,
  type AppointmentTemplateKey,
} from '@/utils/message';
import { buildWhatsappUrl } from '@/utils/whatsapp';
import { useSettingsStore } from '@/stores';
import type { Appointment } from '@/types/appointments';

export interface AppointmentCardProps {
  appointment: Appointment;
  isProcessed?: boolean;
  onMarkProcessed?: () => void;
  rate: number;
  rateDate?: string;
  rateSource?: string;
}

export function AppointmentCard({
  appointment,
  isProcessed = false,
  onMarkProcessed = () => {},
  rate,
  rateDate,
  rateSource,
}: AppointmentCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<AppointmentTemplateKey>(() =>
    getDefaultTemplateKeyForAppointment(appointment)
  );
  const settings = useSettingsStore();

  const timeMode = settings.timeMode || '12h';
  const showBs = settings.showBsPrice && rate > 0;
  const services = (appointment.serviceName || 'Servicio').split(' + ');

  // Dynamically compute active message and WhatsApp URL based on selected quick template
  const activeMessage = messageForAppointment(
    appointment,
    settings,
    rate,
    selectedTemplate
  );
  const activeWhatsappUrl = appointment.customerPhone
    ? buildWhatsappUrl(appointment.customerPhone, activeMessage, settings.whatsappMode)
    : appointment.whatsappUrl;

  const rateParts: string[] = [`Tasa: ${formatBs(rate)}`];
  if (rateDate) {
    rateParts.push(dayjs(rateDate).format('D MMM').replace('.', ''));
  }
  if (rateSource) {
    rateParts.push(rateSource === 'manual' ? 'manual' : formatRateSource(rateSource));
  }
  const rateSummary = rateParts.join(' · ');

  return (
    <div
      className={clsx(
        'bg-surface border rounded-xl p-3.5 sm:p-4 transition-all shadow-xs animate-fade-in',
        isProcessed
          ? 'border-emerald-500/40 dark:border-emerald-500/30 bg-emerald-500/[0.03] dark:bg-emerald-500/[0.02]'
          : 'border-border hover:border-border-hover'
      )}
    >
      {/* Header row: Name + Processed Badge + Strategy Badge */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3
              className={clsx(
                'font-bold text-sm sm:text-base truncate',
                isProcessed ? 'text-text-secondary' : 'text-text-primary'
              )}
            >
              {appointment.customerName}
            </h3>
            {isProcessed && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30 shadow-2xs">
                <Check size={12} className="stroke-[2.5]" />
                <span>Procesado</span>
              </span>
            )}
          </div>
          <div
            className={clsx(
              'flex items-center gap-2 mt-0.5 text-xs flex-wrap transition-opacity',
              isProcessed ? 'text-text-muted opacity-70' : 'text-text-secondary'
            )}
          >
            <span className="flex items-center gap-1 font-medium text-text-primary">
              <Clock
                size={12}
                className={isProcessed ? 'text-emerald-500' : 'text-brand-primary'}
              />
              {formatTime(appointment.startTime, timeMode)}
            </span>
            {appointment.employeeName && (
              <>
                <span className="text-text-muted">•</span>
                <span className="flex items-center gap-1">
                  <User size={12} className="text-text-muted" />
                  {appointment.employeeName}
                </span>
              </>
            )}
          </div>
        </div>

        <StrategyBadge status={appointment.status} reviewRating={appointment.reviewRating} />
      </div>

      {/* Services pills + Pricing */}
      <div
        className={clsx(
          'flex items-center justify-between gap-2 mb-2.5 flex-wrap transition-opacity',
          isProcessed ? 'opacity-70' : 'opacity-100'
        )}
      >
        <div className="flex items-center gap-1.5 flex-wrap min-w-0">
          <Scissors size={13} className="text-text-muted flex-shrink-0" />
          {services.map((srv, idx) => (
            <span
              key={idx}
              className="px-2 py-0.5 bg-surface-secondary text-text-primary rounded-md text-[11px] font-medium border border-border"
            >
              {srv}
            </span>
          ))}
        </div>

        {appointment.totalAmount != null && (
          <div className="ml-auto flex flex-col items-end">
            <div className="flex items-center gap-1 text-xs sm:text-sm font-semibold text-text-primary">
              <CreditCard size={13} className="text-text-muted" />
              <span>{formatCurrency(appointment.totalAmount, settings.currencyCode)}</span>
              {showBs && (
                <span className="text-text-muted font-normal text-[11px]">
                  ({formatBs(convertToBs(appointment.totalAmount, rate, settings.recargo))})
                </span>
              )}
            </div>
            {settings.showLocalBsRate && rate > 0 && (
              <span className="text-[10px] text-text-muted mt-0.5">
                {rateSummary}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Quick Template Switcher */}
      <QuickTemplateSelector
        selected={selectedTemplate}
        onChange={setSelectedTemplate}
        className="mb-2.5"
      />

      {/* Symmetrical Card Actions */}
      <CardActions
        whatsappUrl={activeWhatsappUrl}
        phone={appointment.customerPhone}
        message={activeMessage}
        isProcessed={isProcessed}
        onToggleProcessed={onMarkProcessed}
        expanded={expanded}
        onToggleExpanded={() => setExpanded(!expanded)}
        hasExpandableContent={Boolean(
          appointment.notes ||
            activeMessage ||
            appointment.customerEmail ||
            appointment.salesOrderId
        )}
      />

      {/* Expanded Details Accordion */}
      {expanded && (
        <div className="mt-3 pt-3 border-t border-border space-y-2 text-xs text-text-secondary animate-fade-in">
          {appointment.notes && (
            <div className="p-2 bg-amber-500/10 border border-amber-500/20 rounded-lg text-amber-900 dark:text-amber-200">
              <span className="font-semibold">Notas:</span> {appointment.notes}
            </div>
          )}
          {appointment.customerEmail && (
            <p>
              <span className="font-semibold text-text-primary">Email:</span> {appointment.customerEmail}
            </p>
          )}
          {appointment.salesOrderId && (
            <p>
              <span className="font-semibold text-text-primary">Comprobante de Venta:</span> #{appointment.salesOrderId}
            </p>
          )}
          {activeMessage && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="font-semibold text-text-primary block">Mensaje generado:</span>
                <span className="text-[10px] text-text-muted">
                  ({APPOINTMENT_TEMPLATE_OPTIONS.find((t) => t.key === selectedTemplate)?.label || selectedTemplate})
                </span>
              </div>
              <WhatsAppMessagePreview
                message={activeMessage}
                variant="bubble"
                timestamp={formatTime(appointment.startTime, timeMode)}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
